import { Icon, Text } from '@televet/kibble-ui';
import { Box, BoxProps, Collapse, Flex, useDisclosure } from '@televet/kibble-ui/build/chakra';
import { TaskRepeatIntervalJsonType } from '@televet/shared-types';
import { TaskWithMemoizedValues } from 'pages/Tasks/types';
import React, { useMemo } from 'react';
import { daysOfWeek } from 'shared/constants/daysOfWeek';
import { TaskType } from 'shared/types/graphql';
import { TaskDetail } from '../TaskDetail';

type TaskExpandableDetailCellProps = {
  task: TaskWithMemoizedValues;
  borderLeftColor?: string;
};

export const TaskExpandableDetailCell = ({ task, borderLeftColor }: TaskExpandableDetailCellProps): JSX.Element => {
  const { isOpen, onToggle } = useDisclosure();

  const repeatDefinitionText = useMemo(() => {
    const intervalDefinition: TaskRepeatIntervalJsonType = task.repeatIntervalDefinition;
    if (!intervalDefinition.frequency) {
      return undefined;
    }
    return `${intervalDefinition.frequency} ${
      intervalDefinition.frequency === 'Weekly' &&
      intervalDefinition.dayOfWeek !== undefined &&
      intervalDefinition.dayOfWeek !== null
        ? ` on ${daysOfWeek[intervalDefinition.dayOfWeek]}`
        : ''
    }`;
  }, [task.repeatIntervalDefinition]);

  const isExpandable = useMemo(() => task.type !== TaskType.Custom || task.description, [task.description, task.type]);

  const containerClickableProps: BoxProps = useMemo(
    () =>
      isExpandable
        ? {
            onClick: onToggle,
            cursor: 'pointer',
            'data-testid': `task-detail-toggle-${task.id}`,
          }
        : {},
    [isExpandable, onToggle, task.id],
  );

  return (
    <Box>
      <Flex alignItems="center" gap={2} {...containerClickableProps}>
        {isExpandable && <Icon name={isOpen ? 'chevronDown' : 'chevronRight'} size="sm" />}
        <Text fontSize="sm" ml={isExpandable ? undefined : 6}>
          {task.name}
        </Text>
        {repeatDefinitionText && (
          <Flex gap={1} alignItems="center">
            <Icon name="doubleArrowCircle" size="xs" color="text.subtle" />
            <Text fontSize="xs" color="text.subtle">
              {repeatDefinitionText}
            </Text>
          </Flex>
        )}
      </Flex>
      <Collapse in={isOpen} animateOpacity>
        {isOpen && (
          <Box mt={2} ml={4}>
            <TaskDetail
              task={task}
              borderLeftColor={borderLeftColor}
              showAppointmentDetails={task.type === TaskType.Automation}
            />
          </Box>
        )}
      </Collapse>
    </Box>
  );
};
